import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, Minus, ShoppingCart, UtensilsCrossed, Clock, Flame, Weight, Star } from "lucide-react";
import { useCart } from "./context/CartContext.jsx";
import BackButton from "./components/BackButton.jsx";
import appConfig from "./config";

const ALLERGEN_MAP = {
    gluten:    { label: 'Глютен',      emoji: '🌾' },
    egg:       { label: 'Яйца',        emoji: '🥚' },
    milk:      { label: 'Молоко',      emoji: '🥛' },
    fish:      { label: 'Рыба',        emoji: '🐟' },
    shellfish: { label: 'Моллюски',    emoji: '🦐' },
    nuts:      { label: 'Орехи',       emoji: '🥜' },
    peanuts:   { label: 'Арахис',      emoji: '🫘' },
    soy:       { label: 'Соя',         emoji: '🟡' },
    sesame:    { label: 'Кунжут',      emoji: '🌱' },
    celery:    { label: 'Сельдерей',   emoji: '🥬' },
    mustard:   { label: 'Горчица',     emoji: '🌿' },
    sulfites:  { label: 'Сульфиты',    emoji: '🍷' },
};


const STARS = (rating) => (
    <span>
        {'★'.repeat(rating)}<span className="text-gray-200">{'★'.repeat(5 - rating)}</span>
    </span>
);


const DishDetailPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dish = location.state?.dish;
    const { addItem, increaseQuantity, decreaseQuantity, cartItems } = useCart();
    const [imgError, setImgError]               = useState(false);
    const [imgLoaded, setImgLoaded]             = useState(false);
    const [showReviewsBtn, setShowReviewsBtn]   = useState(() => (dish?.review_count ?? 0) > 0);
    const [reviews, setReviews]                 = useState([]);
    const [reviewsTotal, setReviewsTotal]       = useState(0);
    const [reviewsPage, setReviewsPage]         = useState(1);
    const [reviewsHasMore, setReviewsHasMore]   = useState(false);
    const [loadingMore, setLoadingMore]         = useState(false);
    const reviewsRef = useRef(null);

    useEffect(() => {
        if (!dish?.id) return;
        fetch(`${appConfig.endpoint}/dishes/${dish.id}/reviews?page=1`)
            .then(r => r.json())
            .then(d => {
                const list = d.reviews || [];
                setReviews(list);
                setReviewsTotal(d.total || 0);
                setReviewsHasMore(d.has_more || false);
                setShowReviewsBtn(list.length > 0);
            })
            .catch(() => {});
    }, [dish?.id]);

    const loadMoreReviews = () => {
        const next = reviewsPage + 1;
        setLoadingMore(true);
        fetch(`${appConfig.endpoint}/dishes/${dish.id}/reviews?page=${next}`)
            .then(r => r.json())
            .then(d => {
                setReviews(prev => [...prev, ...(d.reviews || [])]);
                setReviewsHasMore(d.has_more || false);
                setReviewsPage(next);
            })
            .catch(() => {})
            .finally(() => setLoadingMore(false));
    };

    if (!dish) return <div className="p-4 text-gray-500">Блюдо не найдено</div>;

    const scrollToReviews = () => reviewsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const cartItem = cartItems.find(i => i.id === dish.id);
    const qty = cartItem?.quantity || 0;

    const haptic = () => window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
    const handleAdd     = () => { haptic(); addItem(dish); };
    const handleIncrease = () => { haptic(); increaseQuantity(dish.id); };
    const handleDecrease = () => { haptic(); decreaseQuantity(dish.id); };

    const hasImage = dish.image && !imgError;

    const ingredients = Array.isArray(dish.ingredients)
        ? dish.ingredients
        : (typeof dish.ingredients === 'string' && dish.ingredients ? JSON.parse(dish.ingredients) : []);
    const allergens = Array.isArray(dish.allergens)
        ? dish.allergens
        : (typeof dish.allergens === 'string' && dish.allergens ? JSON.parse(dish.allergens) : []);

    // Кнопки действий — общий блок для обоих layout'ов
    const ActionBar = ({ className = '' }) => (
        <div className={className}>
            {qty === 0 ? (
                <div className="flex gap-2">
                    {showReviewsBtn && (
                        <button
                            onClick={scrollToReviews}
                            className="flex items-center justify-center gap-1.5 px-4 py-3.5 rounded-2xl border border-gray-200 text-sm font-medium text-gray-600 active:scale-95 transition-all shrink-0"
                        >
                            <Star size={15} className="text-amber-400 fill-amber-400" />
                            Отзывы
                        </button>
                    )}
                    <button
                        onClick={handleAdd}
                        className="flex-1 bg-brandBlue text-white font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-all shadow-lg shadow-brandBlue/25"
                    >
                        <Plus size={18} strokeWidth={2.5} />
                        Добавить в корзину · {dish.price} ₽
                    </button>
                </div>
            ) : (
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 bg-gray-50 rounded-2xl border border-gray-200 p-1 shrink-0">
                        <button onClick={handleDecrease} className="w-10 h-10 flex items-center justify-center rounded-xl text-gray-500 hover:bg-red-50 hover:text-red-500 transition-colors">
                            <Minus size={16} strokeWidth={2.5} />
                        </button>
                        <span className="text-lg font-bold text-gray-900 min-w-[28px] text-center">{qty}</span>
                        <button onClick={handleIncrease} className="w-10 h-10 flex items-center justify-center rounded-xl bg-brandBlue text-white hover:bg-brandBlue/90 transition-colors">
                            <Plus size={16} strokeWidth={2.5} />
                        </button>
                    </div>
                    <button onClick={() => navigate("/cart")} className="flex-1 bg-brandBlue text-white font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-[0.98] transition-all shadow-lg shadow-brandBlue/25">
                        <ShoppingCart size={18} />
                        В корзину · {(dish.price * qty).toFixed(0)} ₽
                    </button>
                </div>
            )}
        </div>
    );

    // Контент блюда — переиспользуется в обоих layout'ах
    const DishContent = () => (
        <>
            <div className="flex items-start justify-between gap-3 mb-3">
                <h1 className="text-2xl font-bold text-gray-900 leading-snug flex-1">{dish.title}</h1>
                {!hasImage && (
                    <span className="text-xl font-bold text-brandBlue shrink-0 mt-0.5">{dish.price} ₽</span>
                )}
            </div>

            <div className="flex items-center gap-3 flex-wrap mb-4">
                {dish.category && (
                    <span className="inline-block bg-brandBlue/8 text-brandBlue text-xs font-semibold px-3 py-1 rounded-full">
                        {dish.category}
                    </span>
                )}
                {dish.avg_rating > 0 && (
                    <span className="flex items-center gap-1 text-xs text-gray-500 font-medium">
                        <span className="text-amber-400">★</span>
                        {dish.avg_rating}
                        {dish.review_count > 0 && (
                            <span className="text-gray-300 font-normal">({dish.review_count})</span>
                        )}
                    </span>
                )}
            </div>

            {dish.description ? (
                <p className="text-gray-500 text-sm leading-relaxed mb-5">{dish.description}</p>
            ) : (
                <p className="text-gray-300 text-sm italic mb-5">Описание не добавлено</p>
            )}

            {(dish.cooking_time || dish.calories || dish.weight) && (
                <div className="flex flex-wrap gap-2 mb-6">
                    {dish.cooking_time && (
                        <div className="flex items-center gap-1.5 bg-gray-100 rounded-full px-3 py-1.5">
                            <Clock size={12} className="text-gray-400" />
                            <span className="text-xs text-gray-500">{dish.cooking_time} мин</span>
                        </div>
                    )}
                    {dish.calories && (
                        <div className="flex items-center gap-1.5 bg-gray-100 rounded-full px-3 py-1.5">
                            <Flame size={12} className="text-gray-400" />
                            <span className="text-xs text-gray-500">{dish.calories} ккал</span>
                        </div>
                    )}
                    {dish.weight && (
                        <div className="flex items-center gap-1.5 bg-gray-100 rounded-full px-3 py-1.5">
                            <Weight size={12} className="text-gray-400" />
                            <span className="text-xs text-gray-500">{dish.weight} г</span>
                        </div>
                    )}
                </div>
            )}

            {ingredients.length > 0 && (
                <div className="mb-6">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2.5">Состав</p>
                    <div className="flex flex-wrap gap-1.5">
                        {ingredients.map((ing, i) => (
                            <span key={i} className="text-xs text-gray-600 bg-gray-100 rounded-full px-3 py-1.5">{ing}</span>
                        ))}
                    </div>
                </div>
            )}

            {allergens.length > 0 && (
                <div className="bg-gray-50 rounded-2xl p-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2.5">Аллергены</p>
                    <div className="flex flex-wrap gap-1.5">
                        {allergens.map((key, i) => {
                            const a = ALLERGEN_MAP[key] ?? { label: key, emoji: '⚠️' };
                            return (
                                <span key={i} className="inline-flex items-center gap-1 text-xs text-gray-600 bg-white border border-gray-200 rounded-full px-3 py-1.5">
                                    <span className="text-sm">{a.emoji}</span>
                                    {a.label}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}

            {reviews.length > 0 && (
                <div ref={reviewsRef} className="mt-6">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                        Отзывы · {reviewsTotal}
                    </p>
                    <div className="space-y-2.5">
                        {reviews.map((r, i) => (
                            <div key={i} className="bg-gray-50 rounded-2xl p-3.5">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-semibold text-gray-700">{r.author}</span>
                                        <span className="text-amber-400 text-xs">{STARS(r.rating)}</span>
                                    </div>
                                    {r.created_at > 0 && (
                                        <span className="text-xs text-gray-300">
                                            {new Date(r.created_at * 1000).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-600 leading-relaxed">{r.comment}</p>
                            </div>
                        ))}
                    </div>
                    {reviewsHasMore && (
                        <button
                            onClick={loadMoreReviews}
                            disabled={loadingMore}
                            className="mt-3 w-full py-2.5 text-sm text-gray-500 border border-gray-200 rounded-xl active:scale-[0.98] transition-all disabled:opacity-50"
                        >
                            {loadingMore ? 'Загрузка...' : 'Показать ещё'}
                        </button>
                    )}
                </div>
            )}
        </>
    );

    return (
        <>
            {/* ─── Десктоп: два столбца ─── */}
            <div className="hidden md:flex md:h-screen bg-white max-w-4xl mx-auto shadow-sm">

                {/* Левый столбец — фото */}
                <div className="relative w-2/5 shrink-0 bg-gray-100">
                    {hasImage ? (
                        <>
                            {!imgLoaded && <div className="absolute inset-0 skeleton" />}
                            <img
                                src={dish.image}
                                alt={dish.title}
                                onLoad={() => setImgLoaded(true)}
                                onError={() => setImgError(true)}
                                className={`w-full h-full object-cover transition-opacity duration-500 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
                            />
                        </>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <UtensilsCrossed size={64} className="text-gray-200" />
                        </div>
                    )}
                    <div className="absolute top-5 left-4">
                        <BackButton onClick={() => navigate(-1)} variant="glass" />
                    </div>
                </div>

                {/* Правый столбец — контент */}
                <div className="flex flex-col flex-1 overflow-hidden">
                    <div className="flex-1 overflow-y-auto px-8 py-8">
                        {DishContent()}
                    </div>
                    <div className="px-8 py-5 border-t border-gray-100 bg-white">
                        {ActionBar({})}
                    </div>
                </div>
            </div>

            {/* ─── Мобайл: стек ─── */}
            <div className="md:hidden relative min-h-screen bg-white">

                <div className="relative w-full h-[38vh] bg-gray-100 overflow-hidden">
                    {hasImage ? (
                        <>
                            {!imgLoaded && <div className="absolute inset-0 skeleton" />}
                            <img
                                src={dish.image}
                                alt={dish.title}
                                onLoad={() => setImgLoaded(true)}
                                onError={() => setImgError(true)}
                                className={`w-full h-full object-cover transition-opacity duration-500 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
                            />
                        </>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                            <UtensilsCrossed size={64} className="text-gray-200" />
                        </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white via-white/40 to-transparent" />
                    <div className="absolute top-5 left-4 z-10">
                        <BackButton onClick={() => navigate(-1)} variant="glass" />
                    </div>
                    {hasImage && (
                        <div className="absolute bottom-6 right-4 bg-white/90 backdrop-blur-sm px-3.5 py-1.5 rounded-2xl shadow-sm">
                            <span className="text-base font-bold text-gray-900">{dish.price} ₽</span>
                        </div>
                    )}
                </div>

                <div className="relative -mt-5 bg-white rounded-t-3xl px-5 pt-4 pb-32 z-10">
                    <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
                    <DishContent />
                </div>

                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-5 py-4 z-20">
                    <ActionBar />
                </div>
            </div>
        </>
    );
};

export default DishDetailPage;
